import React, { useEffect, useRef, useState } from 'react';
import './cohort-view.styles.scss';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { getCollectionQueryFromDb, getUniqueDocFromDb } from '@/utils/getDataFromDb';
import BackButton from '@/components/back-button/back-button.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAward, faDownload, faRefresh, faUsers } from '@fortawesome/free-solid-svg-icons';
import CohortViewUserList from './cohort-view-user-list.component';
import CohortViewMcList from './cohort-view-mc-list.component';
import CohortViewUser from './cohort-view-user.component';
import ProgressDisplayContainer from '@/components/ADMIN/progress-display/progress-display-container.component';
import CohortViewMc from './cohort-view-mc.component';
import CsvDownload from 'react-json-to-csv';
import { format } from 'date-fns';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { useSelector } from 'react-redux';
import { createPurchaseDataPoint } from '@/utils/create-purchase-data-point';
import { ModalAlert } from '../modal/modal.component';

const CohortView = () => {

    const userProfile = useSelector(state => state.userData.userProfile);
    const { cohortPath } = useParams();
    const [ cohort, setCohort ] = useState(null);
    const [ mcList, setMcList ] = useState(null);
    const [ userList, setUserList ] = useState(null);
    const [ progressNumbers, setProgressNumbers ] = useState(null);
    const [ compiledData, setCompiledData ] = useState(null);
    const [ downloadLoading, setDownloadLoading ] = useState(false);
    const [ view, setView ] = useState(
        window.localStorage.getItem('cohort-view')
        ?? 'users'
    );
    const [ headers ] = useState(
        [
            'fName',
            'lName',
            'email',
            'iein',
            'mc_name',
            'type',
            'date',
            'state',
            'ts',
            'mc_key',
            'id',
            'progressId',
            'cst_key'
        ]
    )
    const [ alertMessage, setAlertMessage ] = useState(null);
    const mcRef = useRef();
    const numbersRef = useRef();
    const userRef = useRef();
    const dataRef = useRef();
    const dataCompleteRef = useRef({})
    const navigate = useNavigate();

    useEffect(() => {
        
        return () => {
            window.localStorage.removeItem('cohort-view');
        }
    }, [])

    useEffect(() => {
        if (!cohortPath) return;
        getCohort();
    }, [cohortPath])

    useEffect(() => {
        if (!cohort) return;
        getMcList();
        getUserList();
    }, [cohort])

    useEffect(() => {
        if (!compiledData) return;
        setDownloadLoading(false);
        document.getElementById('csv-button').click();
        setAlertMessage('Your data has been downloaded!');
    }, [compiledData])

    function getCohort() {
        getCollectionQueryFromDb({
            'path': 'cohorts',
            'param1': {
                'key': 'path',
                'condition': '==',
                'value': cohortPath
            },
            'callback': callback,
            'once': true
        })
        function callback(data) {
            setCohort(Object.values(data)[0]);
        }
    }

    function getMcList() {
        let mc_keys = [...cohort.mcList];
        for (let mc_key of mc_keys) {
            getUniqueDocFromDb({
                'path': `mcs/${mc_key}`,
                'once': true,
                'callback': callback
            })
            getProgressNumbers(mc_key);
        }
        function callback(data) {
            mcRef.current = {
                ...mcRef.current,
                ...{[data.mc_key]: data}
            }
            setMcList(mcRef.current);
        }
    }

    function getProgressNumbers(mc_key) {
        getUniqueDocFromDb({
            'path': `progressNumbers/${mc_key}`,
            'once': true,
            'callback': callback
        })
        function callback(data) {
            numbersRef.current = {
                ...numbersRef.current,
                ...{[mc_key]: data}
            }
            setProgressNumbers(numbersRef.current);
        }
    }

    function getUserList() {
        let cst_keys = [...cohort.userList];
        for (let cst_key of cst_keys) {
            getUniqueDocFromDb({
               'path': `users/${cst_key}`,
               'once': true,
               'callback': callback
            })
        }
        function callback(data) {
            userRef.current = {
                ...userRef.current,
                ...{[data.cst_key]: data}
            }
            setUserList(userRef.current);
        }
    }

    function changeView(e) {
        setView(e);
        window.localStorage.setItem('cohort-view', e);
    }

    function createDownloadData() {
        if (!mcList || !userList) return;
        setDownloadLoading(true);
        getPurchaseData();
        dataCompleteRef.current = {
            'progressCount': Object.values(userList).length * Object.values(mcList).length,
            'progressCompiled': 0,
            'purchasesComplete': false
        }
        let interval = setInterval(() => {
            let counts = {...dataCompleteRef.current};
            if (
                counts.progressCount === counts.progressCompiled &&
                counts.purchasesComplete
            ) {
                clearInterval(interval);
                console.log('done');
                console.log(dataRef.current);
                organizeDownloadData();
            }
        }, 500)
        
        for (let mc_key of Object.keys(mcList)) {
            for (let cst_key of (Object.keys(userList))) {
                getCollectionQueryFromDb({
                    'path': 'compiledData',
                    'param1': {
                        'key': 'progressId',
                        'condition': '==',
                        'value': `${mc_key}_${cst_key}`
                    },
                    'callback': callback,
                    'once': true
                })
                function callback(data) {
                    for (let d of Object.values(data)) {
                        if (d.aEmail) delete d.aEmail;
                        if (d.aName) delete d.aName;
                        d.date = format(new Date(d.ts), "Pp");
                        d.state = userList[cst_key].state;
                        d.mc_name = mcList[mc_key].name;
                        d.email = userList[cst_key].email;
                        d.iein = userList[cst_key].iein ?? "";
                        dataRef.current = {
                            ...dataRef.current,
                            ...{[d.id]: d}
                        }
                    }
                    dataCompleteRef.current.progressCompiled++;
                }
            }
        }
    }

    async function getPurchaseData() {
        const nfeRegs = await UseCloudFunction(
            'getMCRegistrantsFromNFE',
            {
                'cst_key': userProfile.cst_key
            }
        )
        console.log(nfeRegs);
        for (let purchase of nfeRegs.data) {
            const userData = userList[purchase.ind_cst_key];
            if (!userData) continue;
            const mcData = Object.values(mcList).filter(m => m.nfe_code === purchase.evt_code)[0];
            if (!mcData) continue;
            const ts = new Date(purchase['Registrant.reg_add_date']).getTime();
            const obj = createPurchaseDataPoint(
                {'add_date': ts},
                userData,
                mcData
            )
            // console.log(obj);
            if (!obj) continue;
            obj.date = format(new Date(obj.ts), "Pp");
            obj.state = userData.state;
            obj.email = userData.email;
            obj.iein = userData.iein;
            obj.mc_name = mcData.name;
            dataRef.current = {
                ...dataRef.current,
                ...{[obj.id]: obj}
            }
        }
        dataCompleteRef.current.purchasesComplete = true;
    }

    function organizeDownloadData() {
        const tempData = Object.values(dataRef.current);
        const headers = [
            'fName',
            'lName',
            'email',
            'iein',
            'mc_name',
            'type',
            'date',
            'state',
            'ts',
            'mc_key',
            'id',
            'progressId',
            'cst_key'
        ]
        let newArr = [];
        for (let data of tempData) {
            let dataArr = [];
            for (let h of headers) {
                dataArr.push(data[h]);
            }
            newArr.push(dataArr);
        }
        setCompiledData(newArr.sort((a,b) => a.ts < b.ts ? 1 : -1));
        dataRef.current = {};
    }

    return (
        <div className='cohort-view'>
            <BackButton cancel={() => navigate(-1)} />
            <div className="g-space-1"></div>
            <Routes>
                <Route path='' element={
                    (cohort) &&
                    <div className='cohort-content'>
                        <div className='cohort-title'>
                            <div className='cohort-name'>
                                <img 
                                    src={cohort.icon} 
                                    className='cohort-icon' />
                                <div className='section-title'>
                                    {cohort.name}
                                </div>
                            </div>
                            <div className='buttons'>
                                {
                                    (view === 'users')
                                    ?
                                    <button 
                                        className='g-button med-btn'
                                        onClick={() => changeView('mcs')}>
                                        View Micro-Credentials
                                        <FontAwesomeIcon icon={faAward} />
                                    </button>
                                    :
                                    <button 
                                        className='g-button med-btn'
                                        onClick={() => changeView('users')}>
                                        View Cohort Members
                                        <FontAwesomeIcon icon={faUsers} />
                                    </button>
                                }
                                <button 
                                    className='g-button med-btn'
                                    title='Download Data as CSV'
                                    onClick={() => createDownloadData()} >
                                    {
                                        (downloadLoading)
                                        ?
                                        <FontAwesomeIcon icon={faRefresh} className='icn-spinner' />
                                        :
                                        <FontAwesomeIcon icon={faDownload} />
                                    }
                                </button>
                                <CsvDownload 
                                    className='hide'
                                    id="csv-button"
                                    headers={headers}
                                    data={compiledData}
                                    delimiter=","
                                    filename={`${cohort.name}_Data_${format(new Date(), "P").replace("/", "")}.csv`} />
                            </div>
                        </div>
                        <div className="g-space-2"></div>
                        <hr className='no-margin'/>
                        <div className="g-space-0-5"></div>
                        {
                            (userList && mcList && progressNumbers) &&
                            (
                                (view === 'users')
                                ?
                                <CohortViewUserList
                                    cohort={cohort}
                                    userList={userList}
                                    mcList={mcList}
                                    progressNumbers={progressNumbers} />
                                :
                                (view === 'mcs')
                                ?
                                <CohortViewMcList
                                    cohort={cohort}
                                    userList={userList}
                                    mcList={mcList}
                                    progressNumbers={progressNumbers} />
                                : null
                            )
                        }
                    </div>
                }></Route>
                <Route path='user/:cstKey' element={
                    <CohortViewUser
                        cohort={cohort}
                        userList={userList}
                        mcList={mcList}
                        progressNumbers={progressNumbers} />
                }></Route>
                <Route path='mc/:mcKey' element={
                    <CohortViewMc
                        cohort={cohort}
                        userList={userList}
                        mcList={mcList}
                        progressNumbers={progressNumbers} />
                }></Route>
                <Route path='progress/:progressId' element={
                    <ProgressDisplayContainer
                        users={userList}
                        mcList={mcList} />
                }></Route>
            </Routes>
            <ModalAlert
                show={alertMessage}
                cancel={() => setAlertMessage(null)}
                message={alertMessage} />
        </div>
    )
}

export default CohortView